import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../../lib/api';
import { revertErrorMessage } from './inlineEdit';

// Undo a published inline edit (docs/inline-edit-lab.md §S2.3):
// POST /api/edit-requests/:id/revert, then poll GET every 3 s while the
// request is 'reverting' until it is 'reverted' or 'revert_failed'.
//
//   state: 'idle' | 'confirming' | 'undoing' | 'reverted' | 'failed'
//   message: plain-words reason for 'failed' ('' when there is none)
//   retryable: whether asking again may help — an undo that never started,
//              or one the workflow could not finish (revert_failed can be
//              tried again)
//   ask() → confirming, cancel() → idle, confirm() → undoing …
//   initialState 'undoing' picks up an undo that is already running (the
//   Visual Editor after a reload): it only polls.
//
// Used by the Visual Editor panel, the Content Edits page and the admin
// edit-request detail (InlineUndoButton).

export const REVERT_POLL_MS = 3000;

export default function useInlineRevert(requestId, { onReverted, pollMs = REVERT_POLL_MS, initialState = 'idle' } = {}) {
  const [state, setState] = useState(initialState === 'undoing' ? 'undoing' : 'idle');
  const [message, setMessage] = useState('');
  const [retryable, setRetryable] = useState(false);
  const onRevertedRef = useRef(onReverted);
  onRevertedRef.current = onReverted;
  const postDoneRef = useRef(initialState === 'undoing');

  // Another request: start over. (Not on mount — a mount-time reset would
  // race a click that lands before passive effects flush.)
  const lastIdRef = useRef(requestId);
  useEffect(() => {
    if (lastIdRef.current === requestId) return;
    lastIdRef.current = requestId;
    setState('idle');
    setMessage('');
    setRetryable(false);
  }, [requestId]);

  // → true when the request has settled.
  const settleFrom = useCallback((er) => {
    const st = er?.publishState;
    if (st === 'reverted') {
      setState('reverted');
      setMessage('');
      onRevertedRef.current?.(er);
      return true;
    }
    if (st === 'revert_failed') {
      setState('failed');
      setMessage('');
      setRetryable(true);
      return true;
    }
    return false;
  }, []);

  const ask = useCallback(() => { setMessage(''); setState('confirming'); }, []);
  const cancel = useCallback(() => setState('idle'), []);

  const confirm = useCallback(async () => {
    if (!requestId) return;
    postDoneRef.current = false;
    setMessage('');
    setRetryable(false);
    setState('undoing');
    try {
      const data = await api.post(`/api/edit-requests/${requestId}/revert`, {});
      postDoneRef.current = true;
      settleFrom(data?.editRequest);
    } catch (err) {
      postDoneRef.current = true;
      const code = err?.data?.code || err?.code;
      setRetryable(code === 'dispatch_failed' || code === 'publish_in_flight' || !err?.status || err.status >= 500);
      setState('failed');
      setMessage(revertErrorMessage(err));
    }
  }, [requestId, settleFrom]);

  useEffect(() => {
    if (state !== 'undoing' || !requestId) return undefined;
    let errors = 0;
    const t = setInterval(async () => {
      try {
        const data = await api.get(`/api/edit-requests/${requestId}`);
        errors = 0;
        const er = data?.editRequest;
        if (!er || settleFrom(er)) return;
        // Neither undoing nor done once the undo was accepted: it was dropped.
        if (postDoneRef.current && er.publishState !== 'reverting') {
          setState('failed');
          setMessage('');
        }
      } catch {
        errors += 1;
        if (errors >= 5) {
          setState('failed');
          setMessage('We lost contact with the portal. Check your connection, then look again in a few minutes.');
        }
      }
    }, pollMs);
    return () => clearInterval(t);
  }, [state, requestId, pollMs, settleFrom]);

  return { state, message, retryable, ask, cancel, confirm };
}
